import { useModal } from '@pcmp/react-providers';
import { useRouter } from '@/lib/router';
import {
  createContext,
  FC,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

type MenuContext = {
  isMenuOpen: boolean;
  title: string;
  subtitle?: string;
  setProps: (props: { title: string; subtitle?: string }) => void;
  open: () => Promise<void>;
  close: () => Promise<void>;
  goTo: (path: any) => () => void;
};

const menuContext = createContext<MenuContext>({
  isMenuOpen: false,
  title: 'foo',
  setProps: (props: { title: string; subtitle?: string }) => {},
  open: async () => {},
  close: async () => {},
  goTo: (path: any) => () => {},
});

export const useMenuContext = () => useContext(menuContext);

export const MenuProvider: FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  // Hooks
  const router = useRouter();
  const { openModal, data: modals, closeModalById } = useModal();
  // Data
  const isMenuOpen = useMemo(
    () =>
      modals.some((modal) => modal.component.name === 'PageLayoutMenuSider'),
    [modals]
  );
  const [props, setProps] = useState<Pick<MenuContext, 'title' | 'subtitle'>>({
    title: '',
  });
  // Event handlers
  const goTo = useCallback(
    (path: any) => () => {
      handleMenuClose();
      router.push(path);
    },
    [router, modals]
  );
  const handleMenuOpen = useCallback(async () => {
    if (isMenuOpen) return;

    const MenuComponent = await import(
      '@/components/siders/page-layout-menu'
    ).then((mod) => mod.default);

    await openModal({
      component: MenuComponent,
      props: {},
    });
  }, [isMenuOpen, openModal]);
  const handleMenuClose = useCallback(async () => {
    if (!isMenuOpen) return;

    const modal = modals.find(
      (modal) => modal.component.name === 'PageLayoutMenuSider'
    );

    if (modal?.id) closeModalById(modal.id);
  }, [isMenuOpen, modals, closeModalById]);

  const value = useMemo(
    () => ({
      ...props,
      isMenuOpen,
      setProps,
      open: handleMenuOpen,
      close: handleMenuClose,
      goTo,
    }),
    [props, setProps, isMenuOpen, goTo, handleMenuClose, handleMenuOpen]
  );

  return <menuContext.Provider value={value}>{children}</menuContext.Provider>;
};
